/*
 * Live gameplay-settings viewer — read-only mirror of the Discord bot's
 * `/server info` command.
 *
 * Admin picks a server from a dropdown; the backend proxies the
 * Palworld REST API and returns categorised settings. We render them
 * as a glass-card per category, with bool values shown as ✅/❌ and
 * numeric multipliers shown with an `x` suffix.
 */
import { useEffect, useState, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Loader } from "@/components/Atoms";
import { Server, RefreshCw, Loader2 } from "lucide-react";

/** Format a setting value into a player-friendly string. */
function formatValue(v) {
  if (typeof v === "boolean") return v ? "✅ Yes" : "❌ No";
  if (typeof v === "number") {
    // Display "1.5x" / "2x" for rate-style numbers, raw int otherwise.
    if (!Number.isInteger(v)) return `${v.toFixed(2)}x`;
    return String(v);
  }
  if (v == null || v === "") return "—";
  return String(v);
}

/** Colour the value to draw the eye on bools and unusual multipliers. */
function valueClass(v) {
  if (typeof v === "boolean") return v ? "text-emerald-300" : "text-rose-300";
  if (typeof v === "number" && !Number.isInteger(v) && v !== 1) {
    return v > 1 ? "text-amber-300" : "text-teal-300";
  }
  return "text-[var(--bone)]";
}

export function ServerSettings() {
  const toast = useToast();
  const [servers, setServers] = useState([]);
  const [selected, setSelected] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/server-settings");
        if (cancelled) return;
        const list = data.servers || [];
        setServers(list);
        if (list.length > 0 && !selected) setSelected(list[0].name);
      } catch (e) { if (!cancelled) toast.error(apiError(e)); }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async (name) => {
    if (!name) return;
    setLoading(true);
    setData(null);
    try {
      const { data } = await api.get(`/admin/server-settings/${encodeURIComponent(name)}`);
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
      setData({ error: apiError(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (selected) load(selected); }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalSettings = useMemo(() =>
    (data?.categories || []).reduce((a, c) => a + (c.settings?.length || 0), 0)
  , [data]);

  return (
    <div className="space-y-4 fade-up" data-testid="server-settings-root">
      <div className="glass-card p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="flex-1">
          <h2 className="label flex items-center gap-2"><Server size={14} /> Server Gameplay Settings</h2>
          <p className="text-xs text-[var(--muted)] mt-1">
            Live read of the in-game REST API ({data ? `${data.host}:${data.api_port}` : "—"}).
            This page mirrors the bot's <code>/server info</code> command.
          </p>
        </div>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="input sm:w-72"
          data-testid="server-settings-picker"
          disabled={loading || servers.length === 0}
        >
          {servers.length === 0 ? (
            <option value="">No servers configured</option>
          ) : (
            servers.map((s) => (
              <option key={s.name} value={s.name} disabled={!s.has_api}>
                {s.name} {s.has_api ? "" : "(no REST API)"}
              </option>
            ))
          )}
        </select>
        <button
          onClick={() => load(selected)}
          disabled={loading || !selected}
          className="btn btn-ghost"
          data-testid="server-settings-refresh"
          title="Refresh"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </button>
      </div>

      {loading && !data && <Loader />}

      {data?.error && (
        <div className="glass-card p-6 border-l-2 border-rose-500/50">
          <p className="text-rose-300 font-mono text-sm">{data.error}</p>
        </div>
      )}

      {data && !data.error && (
        <>
          <div className="text-xs text-[var(--muted)] -mb-2" data-testid="server-settings-count">
            {totalSettings} settings · {data.categories.length} groups
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            {data.categories.map((cat) => (
              <div key={cat.label} className="glass-card p-4" data-testid={`server-settings-cat-${cat.label.replace(/\s+/g, "-")}`}>
                <h3 className="text-sm font-semibold mb-3 text-[var(--bone)]">{cat.label}</h3>
                <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 text-sm">
                  {cat.settings.map((s) => (
                    <div key={s.key} className="contents" data-testid={`server-setting-${s.key}`}>
                      <dt className="text-[var(--muted)] truncate" title={s.key}>{s.label}</dt>
                      <dd className={`font-mono text-right ${valueClass(s.value)}`}>{formatValue(s.value)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
